import { buildTrackingPayloadBenefit, buildTrackingPayloadCinema, type TrackingDataset } from "./tracking";
import type { FeatureStatus, ActionName, DataContainer } from "@otto-ec/tracking-bct";
import type { DataTrSubmitTrackingDetail } from "@otto-ec/global-resources/tracking";
import type { Debug } from "@otto-ec/global-resources/debug";
import type { HTMLOcCinemaV1Element } from "@otto-ec/otto-components/cinema";

export function addMiniActionsToVisibilityChanges(detail: DataTrSubmitTrackingDetail<"submitMiniAction">, cinemaDom: HTMLOcCinemaV1Element, actionName: ActionName, parentId: string) {
	if (!cinemaDom.visibleItems) return;
	/*                                                   */
	const benefitsToBeTracked = cinemaDom.visibleItems.filter((element) => element.dataset.trackingState !== "visible");
	if (!benefitsToBeTracked.length) return;

	benefitsToBeTracked
		.filter((benefit) => !!benefit.dataset.tracking)
		/*                                                                           */
		.map((benefit) => {
			const tracking = JSON.parse(benefit.dataset.tracking!) as TrackingDataset;
			const featureId = `${parentId}-${tracking.id}${tracking.position}`;
			benefit.dataset.trackingState = "visible";
			detail.parameterMap.updates.push({ featureId, action: actionName });
		});
	detail.parameterMap.updates.push({ featureId: parentId, action: actionName });
}

/**
 *
 */
export function addLoadedTracking(detail: DataTrSubmitTrackingDetail<"submitMerge">, cinemaDom: HTMLOcCinemaV1Element, featureOrder: number, log: Debug<string>, parentId: string, additionalLabels?: DataContainer) {
	const cinemaTracking = buildTrackingPayloadCinema("loaded", cinemaDom, featureOrder, parentId, additionalLabels);
	if (!cinemaTracking || !detail.parameterMap.features) {
		log.info("missing cinema tracking data");
		detail.abort();
		return;
	}
	const features = detail.parameterMap.features;
	features.push(cinemaTracking);

	const benefits = getBenefitEntries(cinemaDom);

	if (!benefits || !cinemaDom.visibleItems) return log.info("no visible items found on cinema");
	benefits.forEach((benefitElement, i) => {
		let status: FeatureStatus = "hidden";
		if (cinemaDom.visibleItems!.includes(benefitElement)) {
			status = "loaded";
		}

		const benefitTracking = buildTrackingPayloadBenefit(status, benefitElement, cinemaTracking.id);
		if (benefitTracking) {
			features.push(benefitTracking);
		} else {
			log.info("missing benefit tracking data for hidden benefit on position", i);
		}
	});
}

export function initBenefitExpiryCleanup(cinemaDom: HTMLElement, maxCleanUpPeriod: number) {
	const benefits = getBenefitEntries(cinemaDom);

	for (let i = 0; i < benefits.length; i++) {
		const benefitDom = benefits[i] as HTMLElement;
		const activeUntil = benefitDom.dataset.activeUntil;
		if (!activeUntil) {
			continue;
		}

		const end: Date = new Date(activeUntil);
		const delayInMillis: number = Math.min(Math.max(end.getTime() - new Date().getTime(), 1), maxCleanUpPeriod);

		window.setTimeout(() => {
			const listElement = benefitDom.closest("div");
			if (listElement) {
				listElement.remove();
			}
			const remainingBenefits = getBenefitEntries(cinemaDom).length;
			if (remainingBenefits === 0) {
				cinemaDom.closest(".ft9_benefit_cinema__root")?.remove();
			}
		}, delayInMillis);
	}
}

/*                                                                                                              */
export function getBenefitEntries(cinemaDom: HTMLElement): HTMLElement[] {
	return [...cinemaDom.querySelectorAll<HTMLElement>(".ft9-benefit-paper-card")];
}

/**
 */
export function updateSheetUrlParams(cinemaDom: HTMLOcCinemaV1Element, params: Record<string, string>) {
	const sheetCreatingElements = [...cinemaDom.querySelectorAll<HTMLElement>("*[data-oc-sheet-v1-create]")];

	for (const element of sheetCreatingElements) {
		const sheetUrlBase64 = element.dataset["ocSheetV1Create.base64Url"] ?? "";
		const sheetUrl = new URL(atob(sheetUrlBase64), window.origin);

		for (const [key, value] of Object.entries(params)) {
			sheetUrl.searchParams.set(key, value);
		}

		element.dataset["ocSheetV1Create.base64Url"] = btoa(sheetUrl.href);
	}
}

export function getClosestParentByDataAttribute(element: HTMLElement, attribute: string): HTMLElement | null {
	return element.closest(`[data-${attribute}]`);
}

/*                                                                                                                           */
export function getElementPositionInPageContext(element: HTMLElement | null, attribute: string): number {
	if (!element) {
		return 99;
	}

	const positionString = getClosestParentByDataAttribute(element, attribute)?.getAttribute(`data-${attribute}`);
	if (!positionString) {
		return 99;
	}

	const position = parseInt(positionString);
	if (Number.isNaN(position) || position < 0) {
		return 99;
	}
	return position;
}

export function removeQuoteEncoding(entity: string): string {
	return entity.replace(/&quot;/g, '""');
}
